/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import once from 'lodash/once';
import classNames from 'classnames';

import {
  clientErrorTracker,
  trans,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';
import { Chip, ImageCarousel } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';

import { OPEN_FULL_SCREEN_PHOTO_LIST } from '@spotahome/ui-library/src/tenant/components/utils/constants';

import { Mediator } from '@spotahome/soyuz-mediator';

import { RATIOS } from '@spotahome/ui-library/src/tenant/components/ImageCarousel/utils/constants';

import { SkeletonSlider } from '../Skeleton';

import {
  getImageTrackIndex,
  getVideoTrackIndex,
  getMapTrackIndex,
  findImageTrack,
  findVideoTrack
} from './utils/sliderAdapter';
import ImageMosaic from './ImageMosaic';

import './MainSliderItem.scss';

const trackEmptyImage = once(() => {
  clientErrorTracker.error({
    error: new Error('Listing images are missing'),
    source: 'Listing-MainSliderItem'
  });
});

const MainSliderItem = ({ tracks = [], onItemClick, deviceType }) => {
  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const createOpenSliderHandler = () => {
    const trackClickedId = getImageTrackIndex(tracks);

    onItemClick(trackClickedId);
  };

  const imageTrack = findImageTrack(tracks);
  const isImageTrackEmpty = !imageTrack?.items.length;
  const videoTrack = findVideoTrack(tracks);
  const videoTrackIndex = getVideoTrackIndex(tracks);
  const mapTrackIndex = getMapTrackIndex(tracks);

  useEffect(() => {
    if (isImageTrackEmpty) {
      trackEmptyImage();
    }
  }, [isImageTrackEmpty]);

  if (isImageTrackEmpty) {
    return <SkeletonSlider />;
  }

  const handleImageMosaicClick = () => {
    if (isListingSimplificationHeaderVariant) {
      Mediator.publish(OPEN_FULL_SCREEN_PHOTO_LIST);
    } else {
      createOpenSliderHandler();
    }
  };

  const hasThreeOrMoreImages = imageTrack?.items?.length >= 3;

  const imageCarouselClasses = classNames('main-slider-image__carousel', {
    'main-slider-image__carousel--listing-simplification':
      isListingSimplificationHeaderVariant
  });

  const hasVideoChip = Boolean(videoTrack);
  const hasMapChip = mapTrackIndex >= 0;

  const chipClasses = classNames('main-slider-image__chip', {
    'main-slider-image__chip--listing-simplification':
      isListingSimplificationHeaderVariant
  });

  const isNonDesktop = deviceType !== 'desktop';

  const showSquareImages = isNonDesktop && isListingSimplificationHeaderVariant;

  const carouselItems = showSquareImages
    ? imageTrack.items.map(item => ({
        ...item,
        src: item.srcSquare
      }))
    : imageTrack.items;

  return deviceType === 'desktop' && hasThreeOrMoreImages ? (
    <ImageMosaic
      imageTrack={imageTrack.items}
      onClick={handleImageMosaicClick}
      onVideoClick={
        hasVideoChip ? () => onItemClick(videoTrackIndex) : undefined
      }
      onMapClick={hasMapChip ? () => onItemClick(mapTrackIndex) : undefined}
    />
  ) : (
    <div
      data-test="main-slider-image-container"
      className={classNames('main-slider-image ga-slider-main-image', {
        'main-slider-image--non-desktop': showSquareImages
      })}
    >
      <ImageCarousel
        onImageClick={createOpenSliderHandler}
        items={carouselItems}
        chevronStyle={isListingSimplificationHeaderVariant ? 'dots' : 'big'}
        preloadRange={0}
        keepHeightWhileLoading={!isNonDesktop}
        withBackgroundChevrons
        className={imageCarouselClasses}
        ratio={showSquareImages ? RATIOS.HOMECARD : RATIOS.PANORAMIC}
      />
      {isListingSimplificationHeaderVariant && (
        <div
          className={classNames('main-slider-image__chips', {
            'main-slider-image__chips--listing-simplification':
              isListingSimplificationHeaderVariant
          })}
        >
          {hasMapChip && (
            <Chip.Choice
              renderLeadingIcon={() => (
                <DSIcon
                  name={isNonDesktop ? 'location_on-1' : 'location_on_filled'}
                />
              )}
              className={chipClasses}
              onClick={e => {
                e.stopPropagation();
                onItemClick(mapTrackIndex);
              }}
              label={trans('slider.carousel.map')}
              size="big"
            />
          )}
          {hasVideoChip && (
            <Chip.Choice
              renderLeadingIcon={() => <DSIcon name="smart_display" />}
              className={chipClasses}
              onClick={e => {
                e.stopPropagation();
                onItemClick(videoTrackIndex);
              }}
              label={trans('slider.carousel.video')}
              size="big"
            />
          )}
          <Chip.Choice
            renderLeadingIcon={() => <DSIcon name="photo_camera" />}
            className={classNames(chipClasses, {
              'main-slider-image__chip__all-photos':
                isListingSimplificationHeaderVariant
            })}
            onClick={handleImageMosaicClick}
            label={trans('slider.carousel.all.photos')}
            size="big"
          />
        </div>
      )}
    </div>
  );
};

MainSliderItem.propTypes = {
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          caption: PropTypes.string,
          src: PropTypes.string.isRequired
        })
      ),
      isHidden: PropTypes.bool
    })
  ),
  onItemClick: PropTypes.func.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired
};

export default MainSliderItem;
